import type {
  ConversationPlacement,
  CreateAssistantInput,
  CrowdexAssistant,
  MoveConversationInput,
  UpdateAssistantInput,
} from '@crowdex/agent-contracts/api';
import { useStore } from 'zustand';
import { createStore, type StoreApi } from 'zustand/vanilla';
import {
  httpWorkspaceDirectoryApi,
  type WorkspaceDirectoryApi,
  type WorkspaceDirectorySnapshot,
} from './directory-client';
import { memoryWorkspaceDirectoryApi } from './memory-directory-api';
import { PREVIEW_WORKSPACE_DIRECTORY } from './preview-directory';

export interface WorkspaceDirectoryState extends WorkspaceDirectorySnapshot {
  status: 'idle' | 'loading' | 'ready' | 'error';
  error: string | null;
  refresh(): Promise<void>;
  createAssistant(input: CreateAssistantInput): Promise<CrowdexAssistant>;
  updateAssistant(id: string, input: UpdateAssistantInput): Promise<CrowdexAssistant>;
  /** The assistant's channel: known once made, else made now. */
  openAssistant(id: string): Promise<string>;
  moveConversation(conversationId: string, input: MoveConversationInput): Promise<void>;
}

function upsert<T extends { id: string }>(list: readonly T[], item: T): T[] {
  return list.some((entry) => entry.id === item.id)
    ? list.map((entry) => (entry.id === item.id ? item : entry))
    : [...list, item];
}

/** An avatar serves one assistant: the one that had `project`'s loses it, as on the server. */
function withoutAvatar(
  list: readonly CrowdexAssistant[],
  project: CrowdexAssistant,
): CrowdexAssistant[] {
  const avatarId = project.avatarId ?? null;
  return list.map((entry) =>
    avatarId !== null && entry.id !== project.id && entry.avatarId === avatarId
      ? { ...entry, avatarId: null }
      : entry,
  );
}

export function createWorkspaceDirectoryStore(
  api: WorkspaceDirectoryApi = httpWorkspaceDirectoryApi,
  initial?: WorkspaceDirectorySnapshot,
): StoreApi<WorkspaceDirectoryState> {
  return createStore<WorkspaceDirectoryState>((set, get) => ({
    projects: initial?.projects ?? [],
    placements: initial?.placements ?? {},
    status: initial ? 'ready' : 'idle',
    error: null,
    async refresh() {
      set({ status: 'loading', error: null });
      try {
        set({ ...(await api.list()), status: 'ready' });
      } catch (error) {
        set({ status: 'error', error: error instanceof Error ? error.message : String(error) });
      }
    },
    async createAssistant(input) {
      const project = await api.createAssistant(input);
      set({ projects: upsert(withoutAvatar(get().projects, project), project) });
      return project;
    },
    async updateAssistant(id, input) {
      const project = await api.updateAssistant(id, input);
      set({ projects: upsert(withoutAvatar(get().projects, project), project) });
      return project;
    },
    async openAssistant(id) {
      const known = get().projects.find((project) => project.id === id)?.conversationId;
      if (known != null) return known;
      const conversationId = await api.openProjectConversation(id);
      set({
        projects: get().projects.map((project) =>
          project.id === id ? { ...project, conversationId } : project,
        ),
      });
      return conversationId;
    },
    async moveConversation(conversationId, input) {
      const placement: ConversationPlacement = await api.moveConversation(conversationId, input);
      set({ placements: { ...get().placements, [conversationId]: placement } });
    },
  }));
}

let sharedStore: StoreApi<WorkspaceDirectoryState> | null = null;

/** `/desktop-preview…`: the shell with the boards' data, no server and no sign-in. */
export function isDesktopPreviewPath(
  pathname: string | undefined = globalThis.location?.pathname,
): boolean {
  return pathname === '/desktop-preview' || pathname?.startsWith('/desktop-preview/') === true;
}

/**
 * The app's directory: the server's, or the boards' in memory on `/desktop-preview…`. Tests
 * install their own with `setWorkspaceDirectoryStore`.
 */
export function workspaceDirectoryStore(): StoreApi<WorkspaceDirectoryState> {
  sharedStore ??= isDesktopPreviewPath()
    ? createWorkspaceDirectoryStore(
        memoryWorkspaceDirectoryApi(PREVIEW_WORKSPACE_DIRECTORY),
        PREVIEW_WORKSPACE_DIRECTORY,
      )
    : createWorkspaceDirectoryStore();
  return sharedStore;
}

export function setWorkspaceDirectoryStore(store: StoreApi<WorkspaceDirectoryState> | null) {
  sharedStore = store;
}

let previewInstalled = false;

/** `/desktop-preview` reads the boards' assistants (not the server's). */
export function installPreviewWorkspaceDirectory(): void {
  if (previewInstalled) return;
  previewInstalled = true;
  // The boards' directory in memory: the preview creates, moves and detaches as the server would.
  setWorkspaceDirectoryStore(
    createWorkspaceDirectoryStore(
      memoryWorkspaceDirectoryApi(PREVIEW_WORKSPACE_DIRECTORY),
      PREVIEW_WORKSPACE_DIRECTORY,
    ),
  );
}

export function useWorkspaceDirectory<T>(selector: (state: WorkspaceDirectoryState) => T): T {
  return useStore(workspaceDirectoryStore(), selector);
}

/** Opens an assistant on its channel, made the first time it is opened. */
export async function openAssistant(
  project: Pick<CrowdexAssistant, 'id' | 'conversationId'>,
  open: (conversationId: string) => void,
): Promise<void> {
  open(
    project.conversationId ??
      (await workspaceDirectoryStore().getState().openAssistant(project.id)),
  );
}

/** The assistants, archived ones left out, the home one first. */
export function liveAssistants(
  state: Pick<WorkspaceDirectoryState, 'projects'>,
): CrowdexAssistant[] {
  return state.projects
    .filter((project) => project.archivedAt === null)
    .sort((left, right) => Number(right.isHome === true) - Number(left.isHome === true));
}
